import { useSelector } from "react-redux";
import { useGetDashboardQuery } from "../dashboard/dashboardApi";
import { selectAuth, selectIsAuthenticated } from "./authSlice";
import { userIdFromToken } from "./tokenClaims";

/**
 * Single source of truth for "what can the current user see/do".
 * Backed by GET /dashboard, which returns the same roles/modules/permissions/
 * branches claims as login - refetched on app load so nav state rebuilds
 * without re-prompting for login (see handoff doc §5).
 */
export const useClaims = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const auth = useSelector(selectAuth);
  const { data, isLoading, isError } = useGetDashboardQuery(undefined, {
    skip: !isAuthenticated,
  });

  return {
    isAuthenticated,
    // The signed-in user's unique id - compare it with a record's own user id
    // (RequestedByUserId, CreatedBy, SubmittedBy...) to decide "is it mine".
    userId: userIdFromToken(auth.token),
    username: auth.username,
    fullName: auth.fullName,
    designation: auth.designation,
    mustChangePassword: auth.mustChangePassword,
    isLoading: isAuthenticated && isLoading,
    isError,
    roles: data?.roles || [],
    modules: data?.modules || [],
    permissions: data?.permissions || [],
    branches: data?.branches || [],
    isAllBranches: Boolean(data?.isAllBranches),
    // The linked employee's own ("mother") branch code - the default for the
    // header branch picker. Null for an account with no employee (admin,
    // CONTROLMSA) and for a session signed in before the API sent it.
    homeBranch: data?.homeBranch || null,
  };
};

export const useHasPermission = (code) => {
  const { permissions } = useClaims();
  if (!code) return true;
  return permissions.includes(code);
};
